import { API } from './interceptor'

/**
 * Phase 4 (46.22) + Part D — show a fix on the page within one findings refresh.
 *
 * Saves that go through Express already mark Ollie's findings out of date.
 * Saves that go through the server (Lambda) can't, so after one succeeds we
 * tell Express which kind of write happened. Either way the page then asks
 * for one fresh findings read. Nothing here is recomputed in the browser.
 *
 * Part D: the write event is included on the client event so every Ollie
 * surface (panel, noticed rows, Success Hub) can drop the same check ids
 * optimistically while Express recomputes — consistent status, no stale alert.
 */

/** Same ids as Express `OLLIE_FINDINGS_WRITE_EVENTS`. */
export const OLLIE_FINDINGS_WRITE_EVENTS = Object.freeze({
  FOOD_SAFETY_LOGGED: 'food_safety_logged',
  DELIVERY_RECEIPTED: 'delivery_receipted',
  INVOICE_SAVED: 'invoice_saved',
  CREDIT_UPDATED: 'credit_updated',
  STOCK_TAKE_SAVED: 'stock_take_saved',
})

/**
 * Mirrors Express registry `refreshOn` for act checks a kitchen write can clear.
 * Keep in sync with `express_app/services/growthEngine/config/ollieChecks.js`.
 */
export const OLLIE_CHECKS_REFRESHED_BY_EVENT = Object.freeze({
  [OLLIE_FINDINGS_WRITE_EVENTS.DELIVERY_RECEIPTED]: Object.freeze([
    'deliveries_not_checked_in',
    'checked_in_not_reconciled',
    'missing_invoices',
  ]),
  [OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED]: Object.freeze([
    'checked_in_not_reconciled',
    'missing_invoices',
    'credits_still_owed',
  ]),
  [OLLIE_FINDINGS_WRITE_EVENTS.CREDIT_UPDATED]: Object.freeze([
    'credits_still_owed',
  ]),
  [OLLIE_FINDINGS_WRITE_EVENTS.FOOD_SAFETY_LOGGED]: Object.freeze([
    'food_safety_out_of_range',
    'food_safety_overdue',
  ]),
  [OLLIE_FINDINGS_WRITE_EVENTS.STOCK_TAKE_SAVED]: Object.freeze([
    'stock_take_overdue',
    'stock_count_unexplained',
    'stock_take_first',
  ]),
})

export const OLLIE_FINDINGS_SAVED_EVENT = 'oms:ollie-findings-saved'

const isWriteEvent = event =>
  Object.values(OLLIE_FINDINGS_WRITE_EVENTS).includes(event)

/** Check ids a write event can clear — same set every Ollie surface should drop. */
export function ollieCheckIdsRefreshedByEvent(event) {
  if (!isWriteEvent(event)) return []
  return [...(OLLIE_CHECKS_REFRESHED_BY_EVENT[event] || [])]
}

/** Tell Express a server-side save changed something Ollie checks. */
export async function markOllieFindingsDirty(venueId, event) {
  if (!venueId) throw new Error('venueId is required')
  if (!isWriteEvent(event)) throw new Error('Unknown Ollie findings event')
  const response = await API.post(
    'api2',
    `growth-engine/venues/${venueId}/ollie/findings/dirty`,
    { body: { event } },
  )
  return { dirty: Boolean(response?.dirty) }
}

/**
 * No venueId means "the venue in view" (helpers that only know an invoice).
 * `event` lets listeners clear the same checks everywhere before the re-read.
 */
export const notifyOllieFindingsSaved = (venueId, event = null) => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(OLLIE_FINDINGS_SAVED_EVENT, {
      detail: {
        venueId: venueId ? String(venueId) : null,
        event: isWriteEvent(event) ? event : null,
      },
    }),
  )
}

/**
 * Call after a save has succeeded. `viaServer` saves nudge Express first and
 * need the venue; Express saves already marked themselves.
 * Never throws: a missed nudge only means the dirty/open refresh path.
 */
export async function ollieFindingsSaved({
  venueId,
  event,
  viaServer = false,
  mark = markOllieFindingsDirty,
} = {}) {
  if (!isWriteEvent(event) || (viaServer && !venueId)) return { ok: false }
  if (viaServer) {
    try {
      await mark(String(venueId), event)
    } catch (error) {
      console.warn('Could not mark Ollie findings out of date', error)
    }
  }
  notifyOllieFindingsSaved(venueId, event)
  return { ok: true, checkIds: ollieCheckIdsRefreshedByEvent(event) }
}
